/**
 * @description Component-TradeAdjustClose
 */
import './index.less';

import { Side } from '@synfutures/sdks-perp';
import { useDebounceFn } from 'ahooks';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowRight from '@/assets/svg/arrow-right.svg?react';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
// import Card from '@/components/Card';
import { FormInput } from '@/components/FormInput';
// import { Message } from '@/components/Message';
import { AvailableBalancePosition } from '@/components/AvailableBalancePosition';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import WrappedButton from '@/components/WrappedButton';
import { FETCHING_STATUS, ZERO } from '@/constants';
import { TRADE_OVERLAY_TYPE, TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useMainPosition } from '@/features/account/positionHook';
import { useGlobalConfig, useIsIpBlocked } from '@/features/global/hooks';
import { useCurrentPairByDevice, useDisplayPairList, useDormantPair } from '@/features/pair/hook';
import {
  resetClosePositionForm,
  resetFormByChainId,
  setClosePositionFormAmount,
  simulateMarketClosePosition,
  switchToMarketTab,
  trade,
} from '@/features/trade/actions';
import {
  useCloseFormStatus,
  useClosePositionFormState,
  useCloseSimulation,
  useMarketFormStateStatus,
  useTradeMaxLeverage,
  useTradeType,
} from '@/features/trade/hooks';
import { useTradeStore } from '@/features/trade/store';
import { useAppProvider, useSDK } from '@/features/web3/hook';
import { useAppDispatch } from '@/hooks';
import { useGa } from '@/hooks/useGa';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import PercentageSelector from '@/pages/components/PercentageSelector/PercentageSelector';
import { GaCategory } from '@/utils/analytics';
import { inputNumChecker, toBN, toWad } from '@/utils/numberUtil';
import TradeFormDetails from '../TradeDetail/TradeFormDetails';
import TradeFormAlert from '../TradeFormAlert';
interface IPropTypes {
  onClose?: () => void;
}
const TradeClose: FC<IPropTypes> = function ({ onClose }) {
  const { isMobile } = useMediaQueryDevice();
  const { showTradeAreaOverlay, setShowTradeAreaOverlay, closePositionPairSymbol } = useTradeStore();

  const dispatch = useAppDispatch();
  const chainId = useChainId();

  const userAddr = useUserAddr();
  const provider = useAppProvider();
  const { t } = useTranslation();
  const gaEvent = useGa();
  const closePositionFormState = useClosePositionFormState(chainId);
  const closeFormStatus = useCloseFormStatus(chainId);
  const tradeType = useTradeType(chainId);

  const ipBlocked = useIsIpBlocked();
  const tradeTypeInDevice = useMemo(() => {
    return tradeType;
  }, [tradeType]);

  const currentPairFromUrl = useCurrentPairByDevice(chainId);
  const pairList = useDisplayPairList(chainId);
  const dormantPairList = useDormantPair(chainId);

  const currentPair = useMemo(() => {
    if (closePositionPairSymbol) {
      return [...pairList, ...dormantPairList].find((p) => p.symbol === closePositionPairSymbol);
    }
    return currentPairFromUrl;
  }, [closePositionPairSymbol, pairList, dormantPairList, currentPairFromUrl]);

  const maxLeverage = useTradeMaxLeverage(currentPair?.maxLeverage);
  const sdk = useSDK(chainId);
  const signer = useWalletSigner();
  const currentPosition = useMainPosition(chainId, userAddr, currentPair?.id);
  const { slippage, deadline } = useGlobalConfig(chainId);
  const marketFormStatus = useMarketFormStateStatus(chainId);
  const isLoading = useMemo(
    () => closeFormStatus === FETCHING_STATUS.FETCHING || marketFormStatus === FETCHING_STATUS.FETCHING,
    [closeFormStatus, marketFormStatus],
  );
  const closeSimulation = useCloseSimulation(chainId);
  // const closeSimulationStatus = useCloseSimulationStatus(chainId);
  const notification = useTxNotification();
  // const closeSection = useCallback(() => {
  //   chainId &&
  //     dispatch(
  //       resetFormByChainId({
  //         chainId,
  //       }),
  //     );
  // }, [chainId, dispatch]);
  const inputAmountStrChanged = useCallback(
    (inputAmountStr: string) => {
      if (Number(inputAmountStr) > 0 && currentPosition?.wrappedSize) {
        if (currentPosition?.wrappedSize.abs().lt(Number(inputAmountStr))) {
          inputAmountStr = currentPosition?.wrappedSize.abs().toString();
        }
      }

      if (chainId) {
        dispatch(
          setClosePositionFormAmount({
            chainId,
            amount: inputAmountStr,
          }),
        );
      }
    },
    [chainId, currentPosition?.wrappedSize, dispatch],
  );
  const percentageChange = useCallback(
    (percentage: number) => {
      let alignPercent = percentage;
      if (percentage > 100) {
        alignPercent = 100;
      }
      const amount = currentPosition?.wrappedSize.abs().mul(alignPercent).div(100).stringValue;
      console.log('🚀 ~ file: index.tsx:62 ~ amount:', { amount, size: currentPosition?.wrappedSize });
      amount && inputAmountStrChanged(amount);
    },
    [inputAmountStrChanged, currentPosition],
  );
  const percentage = useMemo(() => {
    return inputNumChecker(
      WrappedBigNumber.from(closePositionFormState.amount || 0)
        .div(currentPosition?.wrappedSize || 1)
        .mul(100).stringValue,
      currentPosition?.rootInstrument.marginToken.decimals,
    );
  }, [closePositionFormState.amount, currentPosition]);
  const marginTokenAmount = useMemo(() => {
    return ZERO;
  }, []);

  const tradeSide = useMemo(() => {
    return currentPosition?.side === Side.LONG ? Side.SHORT : Side.LONG;
  }, [currentPosition?.side]);

  const onClickCloseButton = useCallback(async () => {
    if (
      closePositionFormState.amount &&
      !toBN(closePositionFormState.amount).eq(0) &&
      chainId &&
      userAddr &&
      closePositionFormState.amount &&
      signer &&
      sdk &&
      currentPair &&
      tradeSide &&
      provider
    ) {
      // const adjustTradeSide = getAdjustTradeSide(tradeSide, currentPair.isInverse);
      const adjustTradeSide = tradeSide;
      try {
        if (closeSimulation?.data) {
          const result = await dispatch(
            trade({
              chainId,
              userAddr,
              signer,
              sdk: sdk,
              pair: currentPair,
              side: adjustTradeSide,
              baseAmount: toWad(closePositionFormState.amount),
              slippage: Number(slippage),
              deadline: Number(deadline),
              tradePrice: closeSimulation?.data?.tradePrice,
              simulation: closeSimulation,
              provider,
              sendTxNotification: notification.open,
            }),
          ).unwrap();
          if (result) {
            gaEvent({
              category: GaCategory.TRADE_PAIR_CARD_WRAPPER,
              action: `Trade-Click on ${tradeSide === Side.LONG ? 'Buy' : 'Sell'}`,
              label: {
                position: (tradeSide === Side.LONG ? '' : '-') + closePositionFormState.amount,
                pairName: currentPair.symbol,
                side: tradeSide === Side.LONG ? 'Buy' : 'Sell',
                tradeType,
              },
            });

            if (result?.status === 1) {
              dispatch(resetClosePositionForm({ chainId }));
              dispatch(resetFormByChainId({ chainId }));
              dispatch(switchToMarketTab({ chainId }));
              onClose && onClose();
            }
          }
        }
      } catch (e) {
        console.log('🚀 ~ file: index.tsx ~ line 106 ~ onClickTradeButton ~ e', e);
      }
    }
  }, [
    closePositionFormState.amount,
    chainId,
    userAddr,
    signer,
    sdk,
    currentPair,
    tradeSide,
    provider,
    closeSimulation,
    dispatch,
    slippage,
    deadline,
    notification.open,
    gaEvent,
    tradeType,
    onClose,
  ]);
  const fetchSimulation = useCallback(() => {
    if (chainId && currentPosition && sdk && userAddr) {
      // const adjustTradeSide = getAdjustTradeSide(tradeSide, currentPair?.isInverse || false);
      const adjustTradeSide = tradeSide;
      // if the position size is 0, we don't need to simulate
      if (currentPosition.size.eq(0) || WrappedBigNumber.from(closePositionFormState.amount || 0).eq(0)) {
        dispatch(resetClosePositionForm({ chainId }));
        return;
      }

      dispatch(
        simulateMarketClosePosition({
          chainId,
          sdkContext: sdk,
          base: closePositionFormState.amount,
          position: currentPosition,
          tradeSide: adjustTradeSide,
          slippage: Number(slippage),
          userAddr,
          maxLeverage,
          isClosePosition: true,
        }),
      );
    }
  }, [
    chainId,
    currentPosition,
    sdk,
    userAddr,
    tradeSide,
    dispatch,
    closePositionFormState.amount,
    slippage,
    maxLeverage,
  ]);
  const { run: simulateTradeDebounced } = useDebounceFn(
    () => {
      fetchSimulation();
    },
    { wait: 500 },
  );
  useEffect(() => {
    (tradeTypeInDevice === TRADE_TYPE.CLOSE_POSITION || showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION) &&
      simulateTradeDebounced();
  }, [
    closePositionFormState.amount,
    simulateTradeDebounced,
    tradeSide,
    currentPair?.fairPrice._hex,
    tradeTypeInDevice,
    showTradeAreaOverlay,
  ]);
  useEffect(() => {
    percentageChange(100);

    // need to reset the form when pair changes
  }, [currentPair?.id]);

  useEffect(() => {
    return () => {
      if (currentPair?.id && chainId) {
        dispatch(resetClosePositionForm({ chainId }));
        dispatch(resetFormByChainId({ chainId }));
        dispatch(switchToMarketTab({ chainId }));
        setShowTradeAreaOverlay(undefined);
      }
    };
    // need to reset the form when pair changes
  }, [currentPair?.id, userAddr]);

  useEffect(() => {
    return () => {
      if (chainId) {
        dispatch(resetClosePositionForm({ chainId }));
        dispatch(resetFormByChainId({ chainId }));
        dispatch(switchToMarketTab({ chainId }));
        setShowTradeAreaOverlay(undefined);
      }
    };
  }, []);

  return (
    <div className="syn-trade-close">
      <div className="syn-trade-close-content">
        <div className="syn-trade-close-content-title">{t('common.size')}</div>
        <FormInput
          disabled={isLoading}
          inputProps={{
            type: 'text',
            pattern: '^[0-9]*[.,]?[0-9]*$',
            inputMode: 'decimal',
            autoComplete: 'off',
            autoCorrect: 'off',
            onKeyDown: (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault(),
            step: 1e18,
          }}
          className="trade-form-content-input"
          inputAmountStr={closePositionFormState.amount || ''}
          inputAmountStrChanged={inputAmountStrChanged}
          suffix={currentPair?.rootInstrument.baseToken.symbol}
        />
        <PercentageSelector disabled={isLoading} value={Number(percentage || 0)} onChange={percentageChange} />
        <AvailableBalancePosition
          showPosition={false}
          overrideTradeType={TRADE_TYPE.CLOSE_POSITION}
          currentPair={currentPair}
        />
      </div>
      {isMobile && (
        <div className="syn-trade-close-leverage">
          <dl>
            <dt>{t('common.leverage')}</dt>
            <div className="syn-trade-adjust-detail-line"></div>
            <dd>
              <EmptyDataWrap isLoading={!currentPosition?.leverageWad}>
                {!closeSimulation?.data
                  ? currentPosition?.wrapAttribute('leverageWad').formatLeverageWithTooltip()
                  : ''}
                {closeSimulation?.data && (
                  <>
                    {currentPosition?.wrapAttribute('leverageWad').formatLeverageWithTooltip()}
                    <ArrowRight />
                    {WrappedBigNumber.from(
                      closeSimulation.data.simulationMainPosition.leverageWad,
                    )?.formatLeverageWithTooltip()}
                  </>
                )}
              </EmptyDataWrap>
            </dd>
          </dl>
        </div>
      )}
      <WrappedButton
        type="primary"
        amount={marginTokenAmount}
        block={true}
        loading={isLoading}
        disabled={!!closeSimulation?.message || closeSimulation?.data?.additionalFee.gt(0) || ipBlocked}
        marginToken={currentPair?.rootInstrument.marginToken}
        afterApproved={onClickCloseButton}>
        {t('common.close')} {closeSimulation?.data ? `@ ` : ''}{' '}
        {closeSimulation?.data
          ? WrappedBigNumber.from(closeSimulation.data.tradePrice).formatPriceNumberWithTooltip()
          : ''}
      </WrappedButton>
      {closeSimulation?.data && (
        // <Spin spinning={closeSimulationStatus === FETCHING_STATUS.FETCHING}>
        <TradeFormDetails
          simulation={closeSimulation?.data}
          marginToken={currentPair?.rootInstrument.marginToken}
          isLoading={false}
          detailToggle={false}
          chainId={chainId}
          fairPrice={currentPair?.wrapAttribute('fairPrice')}
          currentPair={currentPair}
        />
        // </Spin>
      )}
      <TradeFormAlert
        chainId={chainId}
        tradeType={TRADE_TYPE.CLOSE_POSITION}
        tradeSimulation={closeSimulation}
        currentPair={currentPair}
        marginToken={currentPair?.rootInstrument?.marginToken}></TradeFormAlert>
      {/* {closeSimulation?.message && <Message msg={t(closeSimulation?.message || '')} status="warning" />} */}
    </div>
  );
};

export default TradeClose;
